
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Plus } from 'lucide-react';
import { AdminQualification } from '@/types/admin.types';

const AdminQualificationsSection = () => {
  const { watch, setValue } = useFormContext();
  const qualifications = watch('qualifications') || [];

  const addQualification = () => {
    const newQualification: AdminQualification = {
      id: `qual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      degree: '',
      institution: '',
      year: '',
      field: ''
    };
    
    const updatedQualifications = [...qualifications, newQualification];
    setValue('qualifications', updatedQualifications);
  };

  const removeQualification = (qualId: string) => {
    const updatedQualifications = qualifications.filter((qual: AdminQualification) => qual.id !== qualId);
    setValue('qualifications', updatedQualifications);
  };

  const updateQualification = (qualId: string, field: keyof AdminQualification, value: string) => {
    const updatedQualifications = qualifications.map((qual: AdminQualification) => 
      qual.id === qualId ? { ...qual, [field]: value } : qual
    );
    setValue('qualifications', updatedQualifications);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Qualifications
          <Button type="button" variant="outline" size="sm" onClick={addQualification}>
            <Plus className="h-4 w-4 mr-2" />
            Add Qualification
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {qualifications.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            No qualifications added yet. Click "Add Qualification" to get started.
          </p>
        ) : (
          qualifications.map((qual: AdminQualification, index: number) => (
            <div key={qual.id} className="p-4 border rounded-md space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Qualification {index + 1}</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeQualification(qual.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`degree-${qual.id}`}>Degree/Certificate</Label>
                  <Input
                    id={`degree-${qual.id}`}
                    value={qual.degree}
                    onChange={(e) => updateQualification(qual.id, 'degree', e.target.value)}
                    placeholder="e.g., Bachelor of Science"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`field-${qual.id}`}>Field of Study</Label>
                  <Input
                    id={`field-${qual.id}`}
                    value={qual.field}
                    onChange={(e) => updateQualification(qual.id, 'field', e.target.value)}
                    placeholder="e.g., Computer Science"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`institution-${qual.id}`}>Institution</Label>
                  <Input
                    id={`institution-${qual.id}`}
                    value={qual.institution}
                    onChange={(e) => updateQualification(qual.id, 'institution', e.target.value)}
                    placeholder="e.g., Harvard University"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`year-${qual.id}`}>Year</Label>
                  <Input
                    id={`year-${qual.id}`}
                    value={qual.year}
                    onChange={(e) => updateQualification(qual.id, 'year', e.target.value)}
                    placeholder="e.g., 2020"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default AdminQualificationsSection;
